// gallery.component.ts
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppConfigService } from 'app/services/app-config.service';
import { GalleryService, ImageItem, FolderNode, BrowseResult } from 'app/services/gallery/gallery.service';
import { finalize } from 'rxjs';
import { ImageUrlPipe } from '../../pipe/image-url.pipe';

declare var bootstrap: any;

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ImageUrlPipe],
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss']
})
export class GalleryComponent implements OnInit, OnDestroy {

  showLinkModal = false;
  selectedSource: string = '';
  isLinking = false;
  linkResult: string = '';
  linkError: string = '';

  // ── State ──────────────────────────────────────────────────────────────────
  currentPath = '';
  breadcrumbs: { name: string; path: string }[] = [];

  folders: FolderNode[] = [];
  images: ImageItem[] = [];

  searchQuery = '';
  sortMode: 'newest' | 'oldest' | 'name' = 'newest';
  viewMode: 'grid' | 'list' = 'grid';

  // pagination
  pageNumber = 1;
  pageSize = 20;
  totalImages = 0;
  totalPages = 0;
  hasPreviousPage = false;
  hasNextPage = false;

  // upload
  uploading = false;
  uploadPct = 0;
  dragOver = false;

  defaultUrl = '';
  default = '';

  // loading state
  isLoading = false;

  // ✅ Cache Buster — بيتغير كل مرة نرفع صورة
  cacheBuster = Date.now();

  // ✅ Upload Version — بيتغير كل رفعة عشان الـ trackBy
  uploadVersion = 0;

  // ✅ Blob URLs Map — لحفظ الـ blob URLs عشان نمسحهم بعدين
  private blobUrls = new Map<string, string>();

  // selection
  get selectedCount() { return this.images.filter(i => i.selected).length; }
  get totalCount() { return this.totalImages; }

  // modals
  active: ImageItem | null = null;
  lightboxOpen = false;
  editOpen = false;
  deleteOpen = false;

  // folder modal
  newFolderOpen = false;
  newFolderName = '';

  // computed
  get filtered(): ImageItem[] {
    let list = [...this.images];

    if (this.sortMode === 'newest')
      list.sort((a, b) => +new Date(b.date) - +new Date(a.date));

    if (this.sortMode === 'oldest')
      list.sort((a, b) => +new Date(a.date) - +new Date(b.date));

    if (this.sortMode === 'name')
      list.sort((a, b) => a.name.localeCompare(b.name));

    return list;
  }

  // ✅ trackBy function
  trackById = (index: number, item: ImageItem): string => String(item.id);

  constructor(
    private svc: GalleryService,
    private config: AppConfigService,
    private cdr: ChangeDetectorRef
  ) {
    this.default = this.config.default;
    this.defaultUrl = '';
  }

  ngOnInit() {
    this.load('', 1);
  }

  // ✅ تنظيف الـ Blob URLs عند تدمير الـ Component
  ngOnDestroy(): void {
    this.blobUrls.forEach(url => {
      try { URL.revokeObjectURL(url); } catch { }
    });
    this.blobUrls.clear();
  }

  // ============================================================
  // Blob URL Loading — لتخطي الـ Browser Cache
  // ============================================================

  /**
   * بتحمّل الصورة بـ fetch مع cache: 'reload'
   * وبتعملها Blob URL محلي
   */
  private loadImageBlob(img: any): void {
    const originalUrl = img.url;

    if (!originalUrl) return;

    // ✅ امسح الـ blob URL القديم لو موجود
    const oldBlobUrl = this.blobUrls.get(originalUrl);
    if (oldBlobUrl) {
      try { URL.revokeObjectURL(oldBlobUrl); } catch { }
      this.blobUrls.delete(originalUrl);
    }

    // ✅ ابني الـ URL الكامل
    const finalUrl = this.buildFullUrl(originalUrl);

    // ✅ جيب الصورة من السيرفر بدون كاش
    fetch(finalUrl, { cache: 'reload' })
      .then(response => {
        if (!response.ok) throw new Error('Failed to load image');
        return response.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        this.blobUrls.set(originalUrl, blobUrl);
        img.displayUrl = blobUrl;
        this.cdr.detectChanges();
      })
      .catch(err => {
        console.error('❌ Failed to load image:', finalUrl, err);
        img.displayUrl = finalUrl;
        this.cdr.detectChanges();
      });
  }

  /**
   * بيرجع الـ URL الكامل مع الـ base
   */
  private buildFullUrl(url: string): string {
    if (!url) return '';

    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }

    const base = localStorage.getItem('apiURL') ?? '';
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    return `${base}/${cleanUrl}`;
  }

  /**
   * امسح كل الـ Blob URLs المخزّنة
   */
  private clearAllBlobUrls(): void {
    this.blobUrls.forEach(url => {
      try { URL.revokeObjectURL(url); } catch { }
    });
    this.blobUrls.clear();
  }

  // ============================================================
  // Link Uploads
  // ============================================================

  openLinkModal() {
    this.showLinkModal = true;
  }

  closeLinkModal() {
    if (!this.isLinking) {
      this.showLinkModal = false;
    }
  }

  linkUploads() {
    if (!this.selectedSource || this.isLinking) return;

    this.isLinking = true;
    this.linkResult = '';
    this.linkError = '';

    this.svc.repairUploadsLinks(this.selectedSource)
      .pipe(finalize(() => {
        this.isLinking = false;
      }))
      .subscribe({
        next: (res) => {
          console.log(res);
          this.linkResult = `Done successfully. Processed: ${res?.processed ?? 0}, Updated: ${res?.updated_entities ?? 0}`;

          setTimeout(() => {
            const modalEl = document.getElementById('linkSourceModal');
            if (modalEl) {
              const modalInstance = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
              modalInstance.hide();
            }
          }, 1000);
        },
        error: (err) => {
          this.linkError = err?.error?.message || err?.error || 'Something went wrong while linking uploads.';
        }
      });
  }

  // ============================================================
  // Load
  // ============================================================
  load(path: string, page: number = 1) {
    this.currentPath = path;
    this.pageNumber = page;
    this.buildBreadcrumbs(path);

    this.isLoading = true;

    // ✅ امسح الـ blob URLs القديمة قبل الـ load الجديد
    this.clearAllBlobUrls();

    this.svc.browse(
      path,
      this.pageNumber,
      this.pageSize,
      this.searchQuery
    ).subscribe({
      next: (res: any) => {

        this.folders = res.folders ?? [];

        const ts = Date.now();

        this.images = (res.images ?? []).map((img: ImageItem) => ({
          ...img,
          id: `${img.path}__v${this.uploadVersion}__${ts}`,
          size: this.svc.formatSize(img.size as unknown as number),
          selected: false,
          displayUrl: '',   // ✅ فاضي لحد ما نحملها
        }));

        this.totalImages = res.meta?.total_images ?? 0;
        this.totalPages = res.meta?.total_pages ?? 0;
        this.hasPreviousPage = res.meta?.has_previous_page ?? false;
        this.hasNextPage = res.meta?.has_next_page ?? false;

        this.isLoading = false;
        this.cdr.detectChanges();

        // ✅ حمّل الصور بـ fetch بعد ما الـ UI يترسم
        setTimeout(() => {
          this.images.forEach(img => this.loadImageBlob(img));
        }, 0);
      },
      error: (err) => {
        console.error('❌ Load error:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  // ✅ Manual Refresh
  refresh() {
    this.uploadVersion++;
    this.cacheBuster = Date.now();
    this.load(this.currentPath, this.pageNumber);
  }

  onSearchChange() {
    this.load(this.currentPath, 1);
  }

  openFolder(folder: FolderNode) {
    this.load(folder.path, 1);
  }

  buildBreadcrumbs(path: string) {
    this.breadcrumbs = [{ name: 'uploads', path: '' }];
    if (!path) return;

    const parts = path.split('/').filter(Boolean);
    let acc = '';

    parts.forEach(p => {
      acc = acc ? `${acc}/${p}` : p;
      this.breadcrumbs.push({ name: p, path: acc });
    });
  }

  navigateTo(crumb: { name: string; path: string }) {
    this.load(crumb.path, 1);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.pageNumber) return;
    this.load(this.currentPath, page);
  }

  nextPage() {
    if (this.hasNextPage) {
      this.load(this.currentPath, this.pageNumber + 1);
    }
  }

  prevPage() {
    if (this.hasPreviousPage) {
      this.load(this.currentPath, this.pageNumber - 1);
    }
  }

  // =========================
  // Pagination
  // =========================
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const safeTotal = this.totalPages || 1;

    let start = Math.max(1, this.pageNumber - Math.floor(maxVisible / 2));
    let end = Math.min(safeTotal, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  getStartIndex(): number {
    if (this.totalCount === 0) return 0;
    return (this.pageNumber - 1) * this.pageSize + 1;
  }

  getEndIndex(): number {
    const end = this.pageNumber * this.pageSize;
    return Math.min(end, this.totalCount);
  }

  // ── Sort / View ────────────────────────────────────────────────────────────
  setSort(m: 'newest' | 'oldest' | 'name') { this.sortMode = m; }
  setView(m: 'grid' | 'list') { this.viewMode = m; }

  // ── Upload ─────────────────────────────────────────────────────────────────
  onPickFiles(files: FileList | null) {
    if (files?.length) this.doUpload(files);
  }

  onDragOver(e: DragEvent) {
    e.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(e: DragEvent) {
    this.dragOver = false;
  }

  onDrop(e: DragEvent) {
    e.preventDefault();
    this.dragOver = false;
    const files = e.dataTransfer?.files;
    if (files?.length) this.doUpload(files);
  }

  doUpload(files: FileList) {

    const renamedFiles: File[] = Array.from(files).map(file => {
      const lowerName = file.name.toLowerCase();
      return new File([file], lowerName, { type: file.type });
    });

    const dt = new DataTransfer();
    renamedFiles.forEach(f => dt.items.add(f));
    const renamedFileList = dt.files;

    this.uploading = true;
    this.uploadPct = 0;

    const tick = setInterval(() => {
      this.uploadPct = Math.min(this.uploadPct + 10, 90);
    }, 150);

    this.svc.upload(renamedFileList, this.currentPath).subscribe({

      next: () => {
        clearInterval(tick);
        this.uploadPct = 100;

        setTimeout(() => {
          this.uploading = false;
          this.uploadPct = 0;

          // ✅ امسح كل الـ blob URLs القديمة عشان نجبر التحميل من جديد
          this.clearAllBlobUrls();

          this.uploadVersion++;
          this.cacheBuster = Date.now();

          // ✅ Reload فوري
          this.load(this.currentPath, 1);

          // ✅ Reload تاني بعد 1 ثانية للتأكيد
          setTimeout(() => {
            this.clearAllBlobUrls();
            this.uploadVersion++;
            this.cacheBuster = Date.now();
            this.load(this.currentPath, 1);
          }, 1000);

          // ✅ Reload تالت بعد 2 ثانية للاحتياط
          setTimeout(() => {
            this.clearAllBlobUrls();
            this.uploadVersion++;
            this.cacheBuster = Date.now();
            this.load(this.currentPath, 1);
          }, 2000);

          // ✅ scroll لقسم الصور
          setTimeout(() => {
            const imagesSection = document.querySelector('.section-title');
            if (imagesSection) {
              imagesSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 500);

        }, 400);
      },

      error: (err) => {
        console.error('❌ Upload failed:', err);
        clearInterval(tick);
        this.uploading = false;
        this.uploadPct = 0;

        alert(err?.error?.message || 'فشل رفع الصور، حاول تاني');
      },
    });
  }

  // ── Selection ──────────────────────────────────────────────────────────────
  toggleSelect(id: number | string) {
    const i = this.images.find(x => String(x.id) === String(id));
    if (i) {
      i.selected = !i.selected;
      this.cdr.detectChanges();
    }
  }

  toggleSelectAll() {
    const all = this.selectedCount === this.images.length;
    this.images.forEach(i => i.selected = !all);
    this.cdr.detectChanges();
  }

  clearSelection() {
    this.images.forEach(i => i.selected = false);
    this.cdr.detectChanges();
  }

  deleteSelected() {
    const toDelete = this.images.filter(i => i.selected);
    if (!toDelete.length) return;

    let completed = 0;
    const total = toDelete.length;

    toDelete.forEach(img => {
      const realPath = this.getRealPath(img);

      this.svc.deleteImage(realPath).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.clearAllBlobUrls();
            this.uploadVersion++;
            this.cacheBuster = Date.now();
            this.load(this.currentPath, this.pageNumber);
          }
        },
        error: (err) => {
          console.error('❌ Delete failed:', err);
          completed++;
          if (completed === total) {
            this.clearAllBlobUrls();
            this.uploadVersion++;
            this.cacheBuster = Date.now();
            this.load(this.currentPath, this.pageNumber);
          }
        }
      });
    });
  }

  // ✅ استخرج الـ path الأصلي
  private getRealPath(img: ImageItem): string {
    if (img.path) return img.path;
    return String(img.id).split('__v')[0];
  }

  // ── Lightbox ───────────────────────────────────────────────────────────────
  openLightbox(img: ImageItem) {
    this.active = img;
    this.lightboxOpen = true;
  }

  closeLightbox() {
    this.lightboxOpen = false;
  }

  // ── Edit ───────────────────────────────────────────────────────────────────
  openEdit(img: ImageItem) {
    this.active = { ...img };
    this.editOpen = true;
  }

  closeEdit() {
    this.editOpen = false;
  }

  saveEdit() {
    if (!this.active) return;

    const lowerFullName = this.active.name.toLowerCase();
    const nameWithoutExt = lowerFullName.replace(/\.[^/.]+$/, '');

    const realPath = this.getRealPath(this.active);

    this.svc.renameImage(realPath, nameWithoutExt, this.active.desc).subscribe(() => {
      this.clearAllBlobUrls();
      this.uploadVersion++;
      this.cacheBuster = Date.now();
      this.load(this.currentPath, this.pageNumber);
      this.closeEdit();
    });
  }

  // ── Delete single ──────────────────────────────────────────────────────────
  openDelete(img: ImageItem) {
    this.active = img;
    this.deleteOpen = true;
  }

  closeDelete() {
    this.deleteOpen = false;
  }

  confirmDelete() {
    if (!this.active) return;

    const realPath = this.getRealPath(this.active);

    this.svc.deleteImage(realPath).subscribe(() => {
      this.clearAllBlobUrls();
      this.uploadVersion++;
      this.cacheBuster = Date.now();
      this.load(this.currentPath, this.pageNumber);
      this.closeDelete();
    });
  }

  // ── Folder actions ─────────────────────────────────────────────────────────
  openNewFolder() {
    this.newFolderName = '';
    this.newFolderOpen = true;
  }

  closeNewFolder() {
    this.newFolderOpen = false;
  }

  createFolder() {
    const name = this.newFolderName.trim();
    if (!name) return;

    this.svc.createFolder(this.currentPath, name).subscribe(() => {
      this.load(this.currentPath, 1);
      this.closeNewFolder();
    });
  }

  deleteFolder(folder: FolderNode) {
    if (!confirm(`حذف الفولدر "${folder.name}" وكل محتوياته؟`)) return;

    this.svc.deleteFolder(folder.path).subscribe(() => {
      this.clearAllBlobUrls();
      this.uploadVersion++;
      this.cacheBuster = Date.now();
      this.load(this.currentPath, 1);
    });
  }

  // ── Utility ────────────────────────────────────────────────────────────────
  stop(e: Event) {
    e.stopPropagation();
  }
}